import { DietaryRequirements } from "@/clients/models/DietaryRequirements";
import { Medical } from "@/clients/models/Medical";
import { StepsButtons } from "@/components/button/steps-button";
import { MILK_PREFERENCE_MAP, MILK_PREFERENCE_REVERSE_MAP } from "@/constants/milk-preferences";
import { QueryKeys } from "@/constants/query-keys";
import { useGetDietaryRequirement } from "@/hooks/requests/dietary-requirement/use-get-dietary-requirement";
import { useGetDietaryRequirementOptions } from "@/hooks/requests/dietary-requirement/use-get-dietary-requirement-options";
import { useUpdateDietaryRequirement } from "@/hooks/requests/dietary-requirement/use-update-dietary-requirement";
import { useGetAllergyOptions } from "@/hooks/requests/medical/use-get-allergy-options";
import { useGetMedical } from "@/hooks/requests/medical/use-get-medical";
import { useUpdateMedical } from "@/hooks/requests/medical/use-update-medical";
import { useGetMilkOptions } from "@/hooks/requests/milk/use-get-milk-options";
import { useToast } from "@/hooks/ui/use-toast";
import { useStepRouteInfo } from "@/hooks/use-step-route-info";
import { setProfileContext } from "@/utils/sentry/tools";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { AllergensGroup } from "../../components/allergens-group";
import { AnimatedOnboardingContainer } from "../../components/animated-onboarding-container";
import { LifestylePreferenceGroup } from "../../components/lifestyle-preference-group";
import { MilkPreferenceGroup } from "../../components/milk-preference-group";
import { setMixpanelOnboardingEvent } from "../../utils/mixpanel-onboarding";
import { setSmartlookOnboardingEvent } from "../../utils/smartlook-onboarding";

const OnboardingDietaryPreferences = () => {
  const { info, navigateToNextStep } = useStepRouteInfo();
  const queryClient = useQueryClient();
  const { data: allergyOptions } = useGetAllergyOptions();
  const { milkOptions } = useGetMilkOptions();
  const { dietaryOptions } = useGetDietaryRequirementOptions();
  const { medical } = useGetMedical();
  const { dietaryRequirement } = useGetDietaryRequirement();
  const { updateDietaryRequirementAsync, isUpdating: isUpdatingDietary } =
    useUpdateDietaryRequirement();
  const { updateMedicalAsync, isUpdating: isUpdatingMedical } = useUpdateMedical();
  const { showError } = useToast();
  const [selectedDietaryOptions, setSelectedDietaryOptions] = useState<Set<string>>(new Set());
  const [selectedMilkOption, setSelectedMilkOption] = useState<string | null>(null);
  const [selectedAllergyOptions, setSelectedAllergyOptions] = useState<Set<string>>(new Set());
  const isUpdating = isUpdatingDietary || isUpdatingMedical;

  useEffect(() => {
    if (dietaryOptions && dietaryRequirement) {
      const initialDietaryOptions = dietaryOptions.filter((option) =>
        dietaryRequirement.dietaryOptions?.includes(option),
      );
      setSelectedDietaryOptions(new Set(initialDietaryOptions));
    }

    if (dietaryRequirement?.milkPreference) {
      const displayValue = MILK_PREFERENCE_REVERSE_MAP[dietaryRequirement.milkPreference];
      setSelectedMilkOption(displayValue || dietaryRequirement.milkPreference);
    }

    if (allergyOptions && medical) {
      const initialAllergyOptions = allergyOptions.allergyOptions.filter((option) =>
        medical.allergies?.includes(option),
      );
      setSelectedAllergyOptions(new Set(initialAllergyOptions));
    }
  }, [dietaryRequirement, dietaryOptions, milkOptions, allergyOptions, medical]);

  const handleSubmit = async () => {
    const milkPreferenceValue = selectedMilkOption
      ? MILK_PREFERENCE_MAP[selectedMilkOption] || selectedMilkOption
      : null;

    const dietaryDataBody: DietaryRequirements = {
      dietaryOptions: Array.from(selectedDietaryOptions),
      milkPreference: milkPreferenceValue,
    };

    const medicalDataBody: Medical = {
      medicalConditions: medical?.medicalConditions ?? [],
      allergies: Array.from(selectedAllergyOptions),
    };

    try {
      await Promise.all([
        updateDietaryRequirementAsync(dietaryDataBody),
        updateMedicalAsync(medicalDataBody),
      ]);

      queryClient.invalidateQueries({
        queryKey: [QueryKeys.DIETARY_REQUIREMENT],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.MEDICAL],
      });

      setProfileContext(QueryKeys.DIETARY_REQUIREMENT, dietaryDataBody);
      setProfileContext(QueryKeys.MEDICAL, medicalDataBody);

      setSmartlookOnboardingEvent({
        type: "submit",
        stepPath: info?.path,
      });
      setMixpanelOnboardingEvent({
        type: "submit",
        stepPath: info?.path,
      });
      navigateToNextStep();
    } catch (error) {
      if (error instanceof Error) {
        showError(error.message);
      } else {
        showError("An unknown error occurred: " + error);
      }
    }
  };

  const toggleDietaryOption = (option: string) => {
    setSelectedDietaryOptions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(option)) {
        newSet.delete(option);
      } else {
        newSet.add(option);
      }
      return newSet;
    });
  };

  const toggleMilkOption = (option: string) => {
    setSelectedMilkOption((prev) => (prev === option ? null : option));
  };

  const toggleAllergyOption = (option: string) => {
    setSelectedAllergyOptions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(option)) {
        newSet.delete(option);
      } else {
        newSet.add(option);
      }
      return newSet;
    });
  };

  return (
    <AnimatedOnboardingContainer
      title="Is there anything about your diet I should know?"
      handleSubmit={handleSubmit}
      classNames={{
        container: "flex-1 h-full flex flex-col",
        form: "flex-1 flex flex-col overflow-y-scroll no-scrollbar gap-8",
      }}
    >
      <LifestylePreferenceGroup
        selectedOptions={selectedDietaryOptions}
        onToggleOption={toggleDietaryOption}
        disabled={isUpdating}
      />

      <MilkPreferenceGroup
        selectedOptions={selectedMilkOption ? new Set([selectedMilkOption]) : new Set()}
        onToggleOption={toggleMilkOption}
        disabled={isUpdating}
      />

      <AllergensGroup
        selectedOptions={selectedAllergyOptions}
        onToggleOption={toggleAllergyOption}
        disabled={isUpdating}
        maximumDisplayNumber={6}
      />

      <StepsButtons type="button" disabled={isUpdating} onClick={handleSubmit} />
    </AnimatedOnboardingContainer>
  );
};

export default OnboardingDietaryPreferences;
